import { useEffect, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box"
import TextField from "@mui/material/TextField";
import Autocomplete from '@mui/material/Autocomplete';

import { withTranslation } from "react-i18next";
// import { useDispatch } from "react-redux";
import { isEmpty } from "lodash";

import scheduleLogService from "@plannel/services/system/schedule-log-service";
import PaginationContainer from "@plannel/components/PaginationContainer"
import DataState from "@plannel/components/aggrid/DataState"
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import Snackbar from "@plannel/components/Snackbar";
import TextFieldSearch from "@plannel/components/TextFieldSearch";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "../utils/color-util";
import CodeUtil from "../utils/code-util";
import { useDispatch } from "react-redux";
import FilterContainer from "@plannel/components/layout/FilterContainer";

const { snackBarMessageColor } = ColorUtil;
const { scheduleTargetObj, scheduleStatusObj } = CodeUtil;
const scheduleTargetList = Object.values(scheduleTargetObj);
const scheduleStatusList = Object.values(scheduleStatusObj);
const ScheduleHistory = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [50, 100, 200];
  const [currentPage, setCurrentPage] = useState(reduxViewState?.page ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [tPages, setTotalPages] = useState(0);

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const scheduleNameSearch = useRef(reduxViewState?.searchObj?.scheduleName ?? "");
  const [scheduleTargetSearch, setScheduleTargetSearch] = useState(reduxViewState?.searchObj?.scheduleTarget ?? null);
  const [statusSearch, setStatusSearch] = useState(reduxViewState?.searchObj?.status ?? null);

  const [sortParams, setSortParams] = useState({});
  const [scheduleHistories, setScheduleHistories] = useState([]);
  
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const columnDefs = [
    { headerName: "scheduleName", field: "name", type: "nonEditableColumn", width: 140 },
    { headerName: "scheduleTarget", field: "scheduleTarget", type: "nonEditableColumn", valueFormatter: params => GridUtils.gridValueL10N(params?.value), width: 200 },
    { headerName: "status", field: "status", type: "nonEditableColumn", valueFormatter: params => GridUtils.gridValueL10N(params?.value), width: 180 },
    { headerName: "detail", field: "detail", type: "nonEditableColumn", width: 450 },
    { headerName: "jobStartTs", field: "jobStartTs", valueFormatter: dateUtils.formatDateTime, type: "nonEditableColumn" },
    { headerName: "jobEndTs", field: "jobEndTs", valueFormatter: dateUtils.formatDateTime, type: "nonEditableColumn" },
    { headerName: "jobRunTime", field: "jobRunTime", valueFormatter: dateUtils.toTimeString, type: ["nonEditableColumn", "numberColumn"] },
    { headerName: "createdTs", field: "createdTs", valueFormatter: dateUtils.formatDateTime, type: "nonEditableColumn" },
    { headerName: "updatedTs", field: "updatedTs", valueFormatter: dateUtils.formatDateTime, type: "nonEditableColumn" }
  ];


  const getRequestParams = () => {
    const params = {};

    params.scheduleName = scheduleNameSearch.current;

    if (scheduleTargetSearch) {
      params.scheduleTarget = scheduleTargetSearch;
    }

    if (statusSearch) {
      params.status = statusSearch;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    return { ...params, ...sortParams };
  };

  const retrieveDataHistories = () => {
    const params = getRequestParams();
    scheduleLogService.getScheduleLog(params)
      .then(response => {
        const { results, totalPages } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        setScheduleHistories(results);
        setTotalPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const searchObj = { scheduleName: scheduleNameSearch.current, scheduleTarget: scheduleTargetSearch, status: statusSearch };
        const reduxState = reduxUtil.createViewState(viewName, searchObj, currentPage, pageSize);
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      })
  };

  const onGridReady = params => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  };

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveDataHistories();
    }
  };

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveDataHistories();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleTargetSearch, statusSearch]);
  return (
    <>
      <FilterContainer hasIcons={false}>
        <TextFieldSearch sx={{ width: 190, mr: 1 }} label={"scheduleName"} searchCode={scheduleNameSearch} retrieve={initPageToRetrieve} />
        <Autocomplete
          options={scheduleTargetList}
          getOptionLabel={option => t(option)}
          value={scheduleTargetSearch}
          onChange={(_, newValue) => setScheduleTargetSearch(newValue)}
          isOptionEqualToValue={(option, value) => option === value}
          size="small"
          sx={{ mr: 1, minWidth: 190 }}
          renderInput={(params) => (
            <TextField {...params} label={t("scheduleTarget")} />
          )}
        />
        <Autocomplete
          options={scheduleStatusList}
          getOptionLabel={option => t(option)}
          value={statusSearch}
          onChange={(_, newValue) => setStatusSearch(newValue)}
          isOptionEqualToValue={(option, value) => option === value}
          size="small"
          sx={{ mr: 1, minWidth: 190 }}
          renderInput={(params) => (
            <TextField {...params} label={t("status")} />
          )}
        />
      </FilterContainer>
      <Box id="schedule-history-grid" className="ag-theme-balham grid-area" mb={1} >
        <AgGridReact
          ref={gridRef}
          rowData={scheduleHistories}
          {...DefaultGridSetting({ title, viewName })}
          onGridReady={onGridReady}
          onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
        />
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(ScheduleHistory);